import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Trash, MapPin } from "@phosphor-icons/react";

export default function RegionsPage() {
  const [regions, setRegions] = useState([]);
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/regions");
      setRegions(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    try {
      await api.post("/regions", { name: name.trim() });
      setName("");
      toast.success("Region added");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this region?")) return;
    try { await api.delete(`/regions/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="min-h-screen">
      <div className="border-b border-neutral-200 bg-white/80 backdrop-blur-xl sticky top-0 z-10">
        <div className="px-10 py-6">
          <div className="label-eyebrow">Territories</div>
          <h1 className="font-display text-4xl tracking-tight leading-none mt-2">Regions</h1>
        </div>
      </div>

      <div className="p-10 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <form onSubmit={create} className="border border-neutral-200 bg-white p-6 space-y-4" data-testid="region-form">
          <div className="label-eyebrow">Add region</div>
          <Input
            placeholder="e.g. Northeast"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="rounded-none border-neutral-400 h-10"
            data-testid="region-name"
          />
          <Button type="submit" className="w-full h-10 rounded-none bg-neutral-900 text-white hover:bg-neutral-800" data-testid="region-submit">
            <Plus size={16} weight="bold" className="mr-2" /> Add
          </Button>
        </form>

        <div className="lg:col-span-2 border border-neutral-200 bg-white" data-testid="regions-list">
          <div className="border-b border-neutral-200 bg-neutral-50 px-4 py-3 label-eyebrow">Existing regions ({regions.length})</div>
          {loading && <div className="p-6 text-center text-neutral-400 text-sm">Loading…</div>}
          <ul>
            {regions.map((r) => (
              <li key={r.id} className="flex items-center justify-between border-b border-neutral-100 last:border-0 px-4 py-3 hover:bg-neutral-50/60">
                <div className="flex items-center gap-3">
                  <MapPin size={16} weight="duotone" />
                  <span className="font-medium">{r.name}</span>
                </div>
                <button onClick={() => del(r.id)} className="p-1.5 border border-neutral-300 hover:border-red-600 hover:bg-red-600 hover:text-white" data-testid={`delete-region-${r.id}`}>
                  <Trash size={14} weight="bold" />
                </button>
              </li>
            ))}
            {!loading && regions.length === 0 && (
              <li className="p-6 text-center text-neutral-400 text-sm">No regions yet.</li>
            )}
          </ul>
        </div>
      </div>
    </div>
  );
}
